import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { ROUTES } from '@/routes/paths';
import { useMasterItems, useMasterMutations, type MasterItem } from '../hooks/useMasters';

interface MasterConfig {
  label: string;
  description: string;
  /** Set for the geography chain — state -> city -> tehsil -> village. */
  parentType?: string;
}

export const MASTER_CONFIG: Record<string, MasterConfig> = {
  state: { label: 'State', description: 'Top of the geography chain — State → City → Tehsil → Village' },
  city: { label: 'City', description: 'Cities, scoped to a state', parentType: 'state' },
  tehsil: { label: 'Tehsil', description: 'Sub-district, scoped to a city', parentType: 'city' },
  village: { label: 'Village', description: 'Scoped to a tehsil', parentType: 'tehsil' },
  area: { label: 'Area', description: 'Sales/service territory used to classify contacts and enquiries' },
  category: { label: 'Category', description: 'Contact/enquiry classification' },
  grade: { label: 'Grade', description: 'Contact grading (A/B/C etc.)' },
  purpose: { label: 'Visit Purpose', description: 'Reasons for a field visit' },
  dealer: { label: 'Dealer', description: 'Dealer classification list' },
  transportation: { label: 'Transportation', description: 'Shipping/transport modes for challans' },
  complaint_type: { label: 'Complaint Type', description: 'CMS complaint categories' },
  accounting_head: { label: 'Accounting Head', description: 'Simple cashbook head list (not the double-entry chart of accounts)' },
  team: { label: 'Team', description: 'Sales/service team groupings' },
  task_status: { label: 'Task Status', description: 'Custom task status values' },
  task_type: { label: 'Task Type', description: 'Custom task type values' },
  product_division: { label: 'Product Division', description: 'Top-level product classification' },
  product_unit: { label: 'Product Unit', description: 'Units of measure' },
  product_category: { label: 'Product Category', description: 'Automotive / Industrial / Grease, scoped to a division', parentType: 'product_division' },
  product_group: { label: 'Product Group', description: 'Product grouping, scoped to a category', parentType: 'product_category' },
  product_grade: { label: 'Product Grade', description: 'Oil/lubricant grade (e.g. 20W-40, 68, 15W-30)' },
  pack_size: { label: 'Pack Size', description: 'Packaging sizes (e.g. 1 L, 5 L, 210 L Barrel)' },
  api_selector: { label: 'Selector (API / OEM)', description: 'API/OEM service class printed on the pack (SM, SL, SN, CF-4, GL-5) — separate from the viscosity grade' },
  source: { label: 'Source', description: 'Lead/enquiry source list' },
  stage: { label: 'Stage', description: 'Pipeline stage list' },
  lost_reason: { label: 'Lost Reason', description: 'Why a deal was lost — shown when a deal is moved to Closed Lost' },
};

export function GenericMasterPage({ inlineKey }: { inlineKey?: string } = {}) {
  const { masterType: paramType = '' } = useParams<{ masterType: string }>();
  const masterType = inlineKey || paramType;
  const navigate = useNavigate();
  const config = MASTER_CONFIG[masterType];

  const [parentId, setParentId] = useState<string | null>(null);
  // The parent picker is a selection dropdown, not the admin list for that
  // parent type — a deactivated division must disappear from it.
  const { data: parentOptions } = useMasterItems(config?.parentType || '', undefined, true);
  const { data: items = [], isLoading } = useMasterItems(masterType, config?.parentType ? (parentId ?? '') : undefined);
  const { create, update, toggleActive } = useMasterMutations(masterType);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<MasterItem | null>(null);
  const [formName, setFormName] = useState('');
  const [formCode, setFormCode] = useState('');

  if (!config) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">Unknown master type "{masterType}".</p>
        <Button variant="ghost" size="sm" className="mt-3" onClick={() => navigate(ROUTES.masterData)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Master Data
        </Button>
      </div>
    );
  }

  const openNew = () => { setEditing(null); setFormName(''); setFormCode(''); setShowForm(true); };
  const openEdit = (item: MasterItem) => { setEditing(item); setFormName(item.name); setFormCode(item.code || ''); setShowForm(true); };

  const handleSave = () => {
    const name = formName.trim();
    if (!name) { toast.error('Name is required.'); return; }

    if (editing) {
      update.mutate({ id: editing._id, name, code: formCode.trim() }, {
        onSuccess: () => { toast.success('Updated.'); setShowForm(false); },
        onError: () => toast.error('Failed to update.'),
      });
    } else {
      create.mutate({ name, code: formCode.trim(), parentId: config.parentType ? parentId : undefined }, {
        onSuccess: () => { toast.success('Created.'); setShowForm(false); },
        onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to create.'),
      });
    }
  };

  const canAdd = !config.parentType || !!parentId;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(ROUTES.masterData)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{config.label}</h1>
          <p className="text-sm text-muted-foreground">{config.description}</p>
        </div>
      </div>

      {config.parentType && (
        <Card>
          <CardBody className="flex items-center gap-3">
            <span className="text-body-sm font-medium text-on-surface-variant shrink-0">
              {MASTER_CONFIG[config.parentType]?.label}:
            </span>
            <select
              className="h-9 flex-1 rounded border border-outline bg-surface px-3 text-data-md"
              value={parentId ?? ''}
              onChange={(e) => setParentId(e.target.value || null)}
            >
              <option value="">Select {MASTER_CONFIG[config.parentType]?.label.toLowerCase()}…</option>
              {(parentOptions ?? []).map((p) => (
                <option key={p._id} value={p._id}>{p.name}</option>
              ))}
            </select>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader
          title={`${config.label} (${items.length})`}
          action={
            <Button size="sm" onClick={openNew} disabled={!canAdd}>
              <Plus className="h-4 w-4 mr-1" /> New {config.label}
            </Button>
          }
        />
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-6"><Skeleton className="h-40" /></div>
          ) : !canAdd ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              Select a {MASTER_CONFIG[config.parentType!]?.label.toLowerCase()} above to see its {config.label.toLowerCase()} list.
            </p>
          ) : items.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">No {config.label.toLowerCase()} entries yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs uppercase bg-muted/50 border-b">
                  <tr>
                    <th className="px-4 py-3 w-16">S.No</th>
                    <th className="px-4 py-3">Code</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3 text-center">Status</th>
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map((item, idx) => (
                    <tr key={item._id} className="hover:bg-muted/20">
                      <td className="px-4 py-3 text-muted-foreground">{idx + 1}</td>
                      <td className="px-4 py-3 font-mono text-xs">{item.code || '—'}</td>
                      <td className="px-4 py-3 font-medium">{item.name}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => toggleActive.mutate({ id: item._id, isActive: !item.isActive })}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${item.isActive ? 'bg-success' : 'bg-outline'}`}
                          title={item.isActive ? 'Active — click to deactivate' : 'Inactive — click to activate'}
                        >
                          <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${item.isActive ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(item)} title="Edit">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? `Edit ${config.label}` : `New ${config.label}`}
        size="sm"
        footer={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={create.isPending || update.isPending || !formName.trim()}>
              {create.isPending || update.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input label={`${config.label} Name *`} value={formName} onChange={(e) => setFormName(e.target.value)} placeholder={`e.g. ${config.label}`} />
          <Input label="Code (optional)" value={formCode} onChange={(e) => setFormCode(e.target.value)} placeholder="Short code" />
        </div>
      </Modal>
    </div>
  );
}
